/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Badge,
  Box,
  Card,
  Checkbox,
  CircularProgress,
  Divider,
  Flex,
  Heading,
  HStack,
  Image,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  Center,
} from '@chakra-ui/react';
import { getAgreementPlans } from '@/service/subscription/api';
import { getProductsBenefits } from '@/service/club/api';
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { FaCheck } from 'react-icons/fa';
import { useTheme } from '../../../contexts/ThemeProvider';
import { useProfile } from '@/contexts/ProfileProvider';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IPlan } from '@/service/types';
import { APP_EVENTS, spawnAppEvent } from '@/service/utils';
import { Button } from '@/components/atoms/Button';
import { ExternalLink } from '@/components/atoms/ExternalLink';

function Feature({ children }: PropsWithChildren) {
  return (
    <HStack width="100%" spacing={2.5} align="flex-start">
      <Box
        minWidth="20px"
        h="20px"
        borderRadius="full"
        bg="green.50"
        display="flex"
        alignItems="center"
        justifyContent="center"
        mt="1px"
        flexShrink={0}
      >
        <FaCheck color="#38a169" fontSize="9px" />
      </Box>
      <Text fontSize="13px" color="gray.700" lineHeight="1.5">
        {children}
      </Text>
    </HStack>
  );
}

export const badges = [
  { name: 'Bronze', level: 1, image: 'badges/bronze.png' },
  { name: 'Prata', level: 2, image: 'badges/prata.png' },
  { name: 'Ouro', level: 3, image: 'badges/ouro.png' },
  { name: 'Safira', level: 4, image: 'badges/safira.png' },
  { name: 'Diamante', level: 5, image: 'badges/diamante.png' },
];

const MAGAZINE_TIER_TEXT: Record<string, string> = {
  Bronze: '2 revistas: 1 Nacional (PEGN) + 1 Revista SIM+',
  Prata: '3 revistas: 2 Nacionais (PEGN + Época Negócios) + 1 Revista SIM+',
  Ouro: '4 revistas: 3 Nacionais (PEGN + Época Negócios + Galileu) + 1 Revista SIM+',
};

const PLAN_NAME_ORDER = ['Bronze', 'Prata', 'Ouro', 'Safira', 'Diamante'];

function getPlanOrderIndex(name: string) {
  const idx = PLAN_NAME_ORDER.indexOf(name);
  return idx === -1 ? PLAN_NAME_ORDER.length : idx;
}

interface Conv {
  id: number | null;
  nome: string;
  logo: string;
  gif: string;
  hasDiscounts: boolean;
  bookstoreTypes: string[];
  isAudioBook: boolean;
  isSuperApp?: boolean;
  isBookstore?: boolean;
  sectorName?: string;
}

const categories = [
  { id: 6, name: 'Revistas', type: 'magazine' },
  { id: 5, name: 'Infantil', type: 'book' },
  { id: 4, name: 'Educacionais', type: 'book' },
  { id: 2, name: 'Profissionais', type: 'book' },
  { id: 1, name: 'Literários', type: 'book' },
];

let planos = [
  3197, 3198, 3199, 3200, 3201, 3202, 3203, 3204, 1795, 3205, 3206, 3207, 3208, 3209, 3210, 3211, 3212, 3213, 3214,
  3215, 3216, 3217, 3218, 3219, 3220, 3221, 3222, 3223, 3224, 3225, 3226, 3227, 3228, 3229, 3230, 3231, 3232, 3233,
  3234, 3235, 3236, 3237, 3238, 3239, 3240, 3241, 3242, 3243, 3244, 3245, 3246, 3247, 3248, 3249, 3250, 3251, 3252,
  3253, 3254, 3255, 3256, 3257, 3258, 3259, 3260, 3261, 3262, 3263, 3264, 3265, 3266, 3267, 3268, 3269, 3270, 3271,
  3272, 3273, 3274, 3275, 3276, 3277, 3278, 3279, 3280, 3281, 3282, 3283, 3284, 3285, 3286, 3287, 3288, 3289, 3290,
  3291, 3292, 3293, 3294, 3295, 3296, 3297, 3298, 3299, 3300, 3301, 3302, 3303, 3304, 3305, 3306, 3307, 3308, 3309,
  3310, 3311, 3312, 3313, 3314, 3315, 3316, 3317, 3318, 3319, 3320, 3321, 3322, 3323, 3324, 3325, 3326, 3327, 3328,
  3329, 3330, 3331, 3332, 3333, 3334, 3335, 3336, 3337, 3338, 3339, 3340, 3341, 3342, 3343, 3344, 3345, 3346, 3347,
  3348, 3349, 3350, 3351, 3352, 3353, 3354, 3355, 3356, 3357, 3358, 3359, 3360, 3361, 3362, 3363, 3364, 3365, 3366,
  3367, 3368, 3369, 3370, 3371, 3372, 3373, 3374, 3375, 3376, 3377, 3378, 3379, 3380, 3381, 3382, 3383, 3384, 3385,
  3386, 3387, 3388, 3389, 3390, 3391, 3392, 3393, 3394, 3395, 3396, 3397, 3398, 3399, 3400, 3401, 3402, 3403, 3404,
  3405, 3406, 3407, 3408, 3409, 3410, 3411, 3412, 3413, 3414, 3415, 3416, 3417, 3418, 3419, 3420, 3421, 3422, 3423,
  3424, 3425, 3426, 3427, 3428, 3429, 3430, 3431, 3432, 3433, 3434, 3435, 3436, 3437, 3438, 3439, 3440, 3441, 3442,
  3443, 3444, 3445, 3446, 3447, 2588
];

function getProductGif(conv: {
  nome: string;
  bookstoreTypes: string[];
  isAudioBook: boolean;
  hasDiscounts: boolean;
  isSuperApp?: boolean;
}): string {
  if (conv.isSuperApp) return '/assets/benefits/ecommerce.gif';
  if (conv.bookstoreTypes?.includes('magazine')) return '/assets/benefits/revista.gif';
  if (conv.bookstoreTypes?.includes('book')) return '/assets/benefits/livros.gif';
  if (conv.hasDiscounts) return '/assets/benefits/ecommerce.gif';
  if (/tele/i.test(conv.nome)) return '/assets/benefits/telemedicina.gif';
  if (/seguro/i.test(conv.nome)) return '/assets/benefits/seguros.gif';
  if (/cinema/i.test(conv.nome)) return '/assets/benefits/cinema.gif';
  if (/cashback/i.test(conv.nome)) return '/assets/benefits/cashback.gif';
  return '/assets/benefits/lojas.gif';
}

function getMetadataValue(metadata: Record<string, any> | undefined, keys: string[]) {
  for (const key of keys) {
    const value = metadata?.[key]?.value;
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return undefined;
}

function parseIdsFromMetadataValue(rawValue: unknown): number[] {
  if (Array.isArray(rawValue)) {
    return rawValue.map((item) => Number(item)).filter((item) => !Number.isNaN(item));
  }
  if (typeof rawValue === 'number' && !Number.isNaN(rawValue)) {
    return [rawValue];
  }
  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map((item) => Number(item)).filter((item) => !Number.isNaN(item));
        }
      } catch {
        return [];
      }
    }
    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric)) {
      return [numeric];
    }
  }
  return [];
}

function getConvLabel(bookstoreTypes: string[], hasDiscounts: boolean, fallbackName: string) {
  if (bookstoreTypes?.includes('magazine')) return 'Banca';
  if (bookstoreTypes?.includes('book')) return 'Biblioteca';
  if (hasDiscounts) return 'Super App';
  return fallbackName;
}

function getSectorName(metadata: Record<string, any> | undefined, categories?: Array<{ id: number; name: string }>) {
  const rawSectorIds = getMetadataValue(metadata, [
    'sectors',
    'sector',
    'sectorId',
    'sector_id',
    'categoryId',
    'category_id',
  ]);
  const sectorIds = parseIdsFromMetadataValue(rawSectorIds);

  if (sectorIds.length > 0 && categories?.length) {
    const names = sectorIds
      .map((sectorId) => categories.find((category) => category.id === sectorId)?.name)
      .filter((name): name is string => Boolean(name));

    if (names.length > 0) {
      return Array.from(new Set(names)).join(', ');
    }
  }

  const directName = getMetadataValue(metadata, ['category', 'sectorName', 'sectorname']);
  if (typeof directName === 'string' && directName.trim() !== '' && Number.isNaN(Number(directName))) {
    return directName;
  }

  return undefined;
}

/**
 * Monta a URL da pagina de pagamento da Vindi.
 *
 * O plano chega em dois formatos: camelCase quando vem de `getAgreementPlans`
 * (passa pelo snakeCaseDictToCamelCaseDict) e snake_case quando vem de
 * `getProductsBenefits` (/agreement-products/ retorna o dado cru), por isso as
 * duas chaves sao aceitas.
 *
 * O parametro `payload` da Vindi e um JWT assinado gerado pelo backend
 * (`vindi_payload`). O `vindi_token` e a chave usada para assinar esse JWT e
 * NAO deve ir na URL. Sem payload, abrimos a pagina limpa e o cliente preenche
 * os proprios dados.
 */
function getVindiUrl(plan: any): string | null {
  const page = plan?.vindiPage ?? plan?.vindi_page;
  if (!page) return null;

  const payload = plan?.vindiPayload ?? plan?.vindi_payload;
  return payload ? `${page}?payload=${encodeURIComponent(payload)}` : page;
}

function getConvenioPriority(conv: Conv) {
  if (conv.bookstoreTypes?.includes('book')) return 0;
  if (conv.bookstoreTypes?.includes('magazine')) return 1;
  if (conv.isSuperApp) return 2;
  return 3;
}

export function Plans({ mode = 'plans' }: { mode?: 'plans' | 'services' } = {}) {
  const isServicesMode = mode === 'services';
  const { id, logo, colors, isIOS, isApp, hasIntegratedPurchaseOnMobileStore, isProductSuperApp } = useTheme();

  const { profile, subscriptions } = useProfile();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [terms, setTerms] = useState(false);

  const [products, setProducts] = useState<any[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const [selectedConvenio, setSelectedConvenio] = useState<string>();
  const [plans, setPlans] = useState<any>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [convenios, setConvenios] = useState<Conv[]>([]);
  // marca qual convênio já teve a aba auto-focada no plano atual, para não resetar a navegação do usuário
  const focusedConvenioRef = useRef<string | undefined>(undefined);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedProductId = searchParams.get('produto');

  const findCurrentPlanId = useCallback(
    (convenioId?: string) => {
      if (!subscriptions?.length) return undefined;
      const checkId = convenioId || selectedConvenio;
      if (!checkId) return undefined;

      const foundSub = isProductSuperApp
        ? subscriptions.find((sub) => Number(sub.plan.product) === Number(checkId))
        : subscriptions.find((sub) => sub.plan.agreement.id === Number(checkId));

      return foundSub?.plan.id;
    },
    [subscriptions, selectedConvenio, isProductSuperApp]
  );

  const disabledLevel = useMemo(() => {
    return findCurrentPlanId();
  }, [findCurrentPlanId]);

  const handleGetPlans = useCallback(
    async (idConv: string) => {
      setLoadingPlans(true);
      setSelectedTabIndex(0);

      if (isProductSuperApp) {
        const findProduct: any = products?.find((p: any) => p.id === Number(idConv));
        const sortedPlans =
          findProduct?.plans?.sort((a: any, b: any) => getPlanOrderIndex(a.name) - getPlanOrderIndex(b.name)) ?? [];
        setPlans(sortedPlans);
      } else {
        const plansFetch: any = await getAgreementPlans({ agreementID: idConv });
        const hasNestedPlans = plansFetch[0]?.plans;

        let finalPlans: any[];
        if (hasNestedPlans?.length > 0) {
          const superAppEntry: any = plansFetch.find((p: any) => p.defaultProduct?.isSuperapp);
          finalPlans =
            superAppEntry?.plans?.sort((a: any, b: any) => getPlanOrderIndex(a.name) - getPlanOrderIndex(b.name)) ?? [];
        } else {
          finalPlans = plansFetch.sort((a: any, b: any) => getPlanOrderIndex(a.name) - getPlanOrderIndex(b.name));
        }
        setPlans(finalPlans);
      }

      setLoadingPlans(false);
    },
    [isProductSuperApp, products]
  );

  useEffect(() => {
    if (!id || !isProductSuperApp) {
      setProducts([]);
      return;
    }

    const fetch = async () => {
      setLoadingProducts(true);
      try {
        const response = await getProductsBenefits({ agreement_id: id });
        setProducts(Array.isArray(response) ? response : []);
      } catch {
        setProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetch();
  }, [id, isProductSuperApp]);

  const openVindi = useCallback(
    (plan: IPlan) => {
      // App novo (iOS ou Android) com chave RevenueCat avisa pelo User-Agent; iOS antigo sempre comprou na loja
      const hasStorePurchase = isIOS || navigator.userAgent.includes('PartiuStorePurchase');
      if (isApp && hasStorePurchase && hasIntegratedPurchaseOnMobileStore) {
        navigate(`/perfil/planos/purchase-subscription-mobile/${btoa(profile?.cpf + ':' + id)}`);
        return;
      }

      const vindiUrl = getVindiUrl(plan);

      if (!vindiUrl) return;

      // O Safari (desktop e iOS) so libera abrir aba nova de forma SINCRONA dentro
      // do gesto do usuario. Se o window.open sair de dentro de um setTimeout/await,
      // ele cai no bloqueador de pop-up e falha em silencio -- por isso abrimos aqui,
      // direto no clique, e deixamos o loading/refetch para depois.
      if (isApp) {
        spawnAppEvent(APP_EVENTS.OPEN_LINK, vindiUrl);
      } else {
        const vindiWindow = window.open(vindiUrl, '_blank');

        if (vindiWindow) {
          // sem a feature 'noopener' no window.open para conseguir detectar bloqueio
          // (varios browsers retornam null quando ela e passada)
          vindiWindow.opener = null;
        } else {
          // pop-up bloqueado mesmo assim: navega na propria aba como fallback
          window.location.assign(vindiUrl);
          return;
        }
      }

      setLoading(true);

      setTimeout(() => {
        setLoading(false);
        queryClient.refetchQueries('profileSubscriptions');
      }, 2000);
    },
    [isApp, isIOS, hasIntegratedPurchaseOnMobileStore, navigate, profile?.cpf, id, queryClient]
  );

  React.useEffect(() => {
    if (!isProductSuperApp) {
      return;
    }

    if (products && isProductSuperApp) {
      let conv: Conv[] = [];
      products.forEach((product: any) => {
        const defaultProduct = product.defaultProduct ?? product.default_product;

        if (!defaultProduct) return;

        if (isServicesMode) {
          if (defaultProduct.id === 2 || defaultProduct.id === 3) return;
          if (product.metadata?.hasaudiobook?.value === 'true') return;
        }

        let bookstoreTypes = [];
        if (defaultProduct.id === 3 || defaultProduct.type === 'book') {
          bookstoreTypes.push('book');
        } else if (defaultProduct.id === 2 || defaultProduct.type === 'magazine') {
          bookstoreTypes.push('magazine');
        }

        let isAudioBook = false;
        if (product.metadata?.hasaudiobook?.value === 'true') {
          isAudioBook = true;
        }

        const hasDiscounts =
          product.metadata?.localmodule?.value === 'true' || product.metadata?.ecommercemodule?.value === 'true';
        const sectorName = getSectorName(product.metadata, categories);
        const productName = getConvLabel(bookstoreTypes, hasDiscounts, defaultProduct?.name || product.name);
        const item: Conv = {
          id: product.id,
          nome: productName,
          logo:
            product.metadata?.logo?.value && product.metadata?.logo?.value !== ''
              ? product.metadata?.logo?.value
              : logo,
          gif: '',
          hasDiscounts,
          bookstoreTypes: bookstoreTypes,
          isAudioBook: isAudioBook,
          isSuperApp: defaultProduct?.isSuperapp ?? defaultProduct?.is_superapp,
          isBookstore: !!(defaultProduct?.isBookstore ?? defaultProduct?.is_bookstore),
          sectorName,
        };
        item.gif = getProductGif(item);
        conv.push(item);
      });

      if (conv.length > 0 && conv[0]?.id !== null) {
        const first =
          conv.find((c) => requestedProductId && c.id?.toString() === requestedProductId) ||
          // whitelist esconde o seletor de convênio: mostra o Bronze do produto super app
          (planos.includes(id || 0) && conv.find((c) => c.isSuperApp)) ||
          conv.find((c) => c.isBookstore) ||
          conv[0];
        const selectedId = first?.id != null ? first.id.toString() : undefined;
        if (selectedId) {
          setSelectedConvenio(selectedId);
          handleGetPlans(selectedId);
        }
      }
      setConvenios(conv);
    }
  }, [isProductSuperApp, isServicesMode, logo, products]);

  const visiblePlans = useMemo(
    () =>
      plans.filter((p: any) => {
        if (p.name === 'Light') return false;
        const isCurrentPlan = disabledLevel === p.id;
        const hasValue = Number(p.value) > 0;
        return hasValue || isCurrentPlan;
      }),
    [plans, disabledLevel]
  );

  const sortedConvenios = useMemo(
    () =>
      [...convenios].sort((a, b) => {
        const aPriority = getConvenioPriority(a);
        const bPriority = getConvenioPriority(b);
        if (aPriority !== bPriority) return aPriority - bPriority;
        return (a.id || 0) - (b.id || 0);
      }),
    [convenios]
  );

  const convenioGroups = useMemo(
    () =>
      [
        { title: 'Produtos', items: sortedConvenios.filter((c) => c.isBookstore) },
        { title: 'Serviços', items: sortedConvenios.filter((c) => !c.isBookstore) },
      ].filter((g) => g.items.length > 0),
    [sortedConvenios]
  );

  const PLAN_TYPE_MESSAGES = {
    descontos: [
      (p: any) => `Acesso para ${p.access_amount} usuário(s) na plataforma.`,
      () => 'Revista Personalizada com conteúdos exclusivos.',
      () => 'Dicas de estabelecimentos do mês.',
      () => 'Um super clube de vantagens para os assinantes da revista. Aproveite:',
      () => 'Cinemas.',
      () => 'Parceiros Ecommerce.',
      () => 'Parceiros físicos e muito mais.',
    ],
    sva: [(p: any) => `Acesso para ${p.access_amount} usuário(s) na plataforma.`],
    magazine: [
      (p: any) => `Compra recorrente de ${p.magazine_quantity} revista(s) digital(is) à escolha do usuário.`,
      () => 'Seleção de edições disponíveis no catálogo.',
      () => 'Identificação editorial com ISSN.',
      () => 'Download definitivo para uso pessoal.',
      () => 'Possibilidade de troca por outra edição na banca.',
      (p: any) => `Acesso à plataforma digital para leitura online para ${p.access_amount} usuário(s).`,
    ],
    livro: [
      (p: any) => `Compra recorrente de ${p.book_quantity} ebook(s) digital(is) à escolha do usuário.`,
      () => 'Seleção de obras disponíveis no catálogo.',
      () => 'Identificação editorial com ISBN.',
      () => 'Download definitivo para uso pessoal.',
      () => 'Possibilidade de troca por outro título no acervo.',
      (p: any) => `Acesso à biblioteca digital da plataforma para leitura online para ${p.access_amount} usuário(s).`,
    ],
    audiobook: [
      (p: any) =>
        `Compra recorrente de ${p.book_quantity} ebook(s) e ${p.audiobook_quantity} audiobook(s) digitais à escolha do usuário.`,
      () => 'Seleção de obras disponíveis no catálogo.',
      () => 'Identificação editorial com ISBN.',
      () => 'Download definitivo para uso pessoal.',
      () => 'Possibilidade de troca por outro título no acervo.',
      (p: any) => `Acesso à biblioteca digital da plataforma para leitura online para ${p.access_amount} usuário(s).`,
    ],
  };

  function getPlanType(conv: Conv): keyof typeof PLAN_TYPE_MESSAGES {
    if (isServicesMode) return 'descontos';
    if (conv.hasDiscounts) return 'descontos';
    if (conv.bookstoreTypes?.includes('magazine')) return 'magazine';
    if (conv.isAudioBook) return 'audiobook';
    if (conv.bookstoreTypes?.includes('book')) return 'livro';
    if (!conv.isSuperApp) return 'sva';
    return 'descontos';
  }

  function PlanFeatures({ conv, plan }: { conv: Conv; plan: any }) {
    const planType = getPlanType(conv);
    const messages = PLAN_TYPE_MESSAGES[planType];
    const tierText = planType === 'magazine' ? MAGAZINE_TIER_TEXT[plan?.name] : undefined;
    const includes = tierText ? [() => tierText, ...messages.slice(1)] : messages.slice(1);
    if (includes.length === 0) return null;
    return (
      <VStack align="flex-start" spacing={2} width="100%">
        <Text fontSize="11px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.8px">
          O que está incluído
        </Text>
        {includes.map((msgFn, idx) => (
          <Feature key={idx}>{msgFn(plan)}</Feature>
        ))}
      </VStack>
    );
  }

  function Loading() {
    return (
      <Center width="100%" height="40vh">
        <CircularProgress isIndeterminate color={colors.primaryColor} />
      </Center>
    );
  }

  const isInPlanosWhitelist = planos.includes(id || 0);

  // Auto-foca a aba do plano atual apenas uma vez por convênio (ao carregar seus planos).
  // Depois disso o usuário pode navegar livremente entre as abas sem ser resetado.
  useEffect(() => {
    if (!disabledLevel || visiblePlans.length === 0) return;
    if (focusedConvenioRef.current === selectedConvenio) return;

    const filteredPlans = visiblePlans.filter((p: any) => {
      if (!isInPlanosWhitelist) return true;
      return p.name === 'Bronze' || disabledLevel === p.id;
    });

    const activeIndex = filteredPlans.findIndex((p: any) => p.id === disabledLevel);
    if (activeIndex !== -1) {
      setSelectedTabIndex(activeIndex);
    }
    focusedConvenioRef.current = selectedConvenio;
  }, [disabledLevel, visiblePlans, isInPlanosWhitelist, selectedConvenio]);

  if (loadingProducts) {
    return (
      <Center width="100%" height="40vh">
        <CircularProgress isIndeterminate color={colors.primaryColor} />
      </Center>
    );
  }

  return (
    <VStack
      spacing={0}
      gap={{ base: 3, md: 4 }}
      flexDirection={{ base: 'column', md: 'row' }}
      maxWidth={isInPlanosWhitelist ? '640px' : '100%'}
      align="flex-start"
      width="100%"
    >
      {!isInPlanosWhitelist && (
        <Box width={{ base: '100%', md: '37%' }} minWidth={{ md: '220px' }} flexShrink={0}>
          {convenioGroups.map((group) => (
            <Box key={group.title} display={{ base: 'block', md: 'none' }} width={'100%'}>
              <Text
                fontSize="10px"
                fontWeight="700"
                color="gray.400"
                textTransform="uppercase"
                letterSpacing="1px"
                textAlign="center"
                pb={1}
              >
                {group.title}
              </Text>
              <Box
                display="flex"
                flexWrap="wrap"
                gap={2}
                pb={2}
                width={'100%'}
                justifyContent={'center'}
                sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
              >
                {group.items.map((c) => {
                  const isSelected = selectedConvenio === c.id?.toString();
                  return (
                    <Box
                      key={c.id}
                      onClick={() => {
                        if (c.id) {
                          setSelectedConvenio(c.id.toString());
                          handleGetPlans(c.id.toString());
                        }
                      }}
                      cursor="pointer"
                      borderRadius="14px"
                      border="2px solid"
                      borderColor={isSelected ? colors.primaryColor : 'gray.100'}
                      bg={'white'}
                      boxShadow={isSelected ? `0 4px 16px ${colors.primaryColor}30` : '0 2px 8px rgba(0,0,0,0.06)'}
                      transition="all 0.18s ease"
                      _hover={{ transform: 'translateY(-2px)', boxShadow: `0 6px 18px ${colors.primaryColor}25` }}
                      minWidth="82px"
                      maxWidth="82px"
                      overflow="hidden"
                      flexShrink={0}
                      p="8px 6px"
                    >
                      <VStack spacing={1.5} align="center">
                        <Box
                          position={'relative'}
                          width="38px"
                          height="38px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Image
                            src={c.gif}
                            alt={c.nome}
                            h="32px"
                            w="32px"
                            objectFit="contain"
                            position="relative"
                            zIndex={1}
                            margin="auto"
                          />
                          <Box
                            position={'absolute'}
                            top={0}
                            left={0}
                            width="100%"
                            height={'100%'}
                            borderRadius="50%"
                            bg={colors.primaryColor}
                            mixBlendMode={'color'}
                            zIndex={2}
                            pointerEvents="none"
                          />
                        </Box>

                        <Text
                          fontSize="10px"
                          fontWeight="600"
                          textAlign="center"
                          noOfLines={2}
                          lineHeight="1.2"
                          color="gray.800"
                        >
                          {c.nome}
                        </Text>
                        {c.sectorName && c.sectorName !== c.nome && (
                          <Text
                            fontSize="9px"
                            color={colors.primaryColor}
                            fontWeight="600"
                            textAlign="center"
                            noOfLines={1}
                          >
                            {c.sectorName}
                          </Text>
                        )}
                      </VStack>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}

          {/* Desktop: vertical list */}
          <VStack
            display={{ base: 'none', md: 'flex' }}
            spacing={1.5}
            align="stretch"
            bg="white"
            borderRadius="16px"
            border="1px solid"
            borderColor="gray.100"
            p="14px"
            boxShadow="0 2px 12px rgba(0,0,0,0.06)"
            alignSelf="flex-start"
          >
            {convenioGroups.map((group) => (
              <React.Fragment key={group.title}>
                <Text
                  fontSize="10px"
                  fontWeight="700"
                  color="gray.400"
                  textTransform="uppercase"
                  letterSpacing="1px"
                  px={1}
                  pb={1}
                >
                  {group.title}
                </Text>
                {group.items.map((c) => {
                  const isSelected = selectedConvenio === c.id?.toString();
                  return (
                    <Flex
                      key={c.id}
                      onClick={() => {
                        if (c.id) {
                          setSelectedConvenio(c.id.toString());
                          handleGetPlans(c.id.toString());
                        }
                      }}
                      cursor="pointer"
                      borderRadius="10px"
                      px={2.5}
                      py={2}
                      gap={2.5}
                      align="center"
                      bg={'transparent'}
                      border="1.5px solid"
                      borderColor={isSelected ? `${colors.primaryColor}50` : 'transparent'}
                      transition="all 0.15s ease"
                      _hover={{ bg: isSelected ? `${colors.primaryColor}12` : 'gray.50' }}
                    >
                      <Box
                        position="relative"
                        width="38px"
                        height="38px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        mx="auto"
                      >
                        <Image
                          src={c.gif}
                          alt={c.nome}
                          h="32px"
                          w="32px"
                          objectFit="contain"
                          position="relative"
                          zIndex={1}
                          margin="auto"
                        />
                        <Box
                          position={'absolute'}
                          top={0}
                          left={0}
                          width="100%"
                          height={'100%'}
                          borderRadius="50%"
                          zIndex={2}
                          pointerEvents="none"
                          // Só aplica cor + blend onde o WebView suporta mix-blend-mode.
                          // Sem suporte, o overlay fica transparente e o ícone original
                          // aparece normal (em vez de virar um bloco sólido).
                          sx={{
                            '@supports (mix-blend-mode: color)': {
                              backgroundColor: colors.primaryColor,
                              mixBlendMode: 'color',
                            },
                          }}
                        />
                      </Box>
                      <Box flex={1} overflow="hidden">
                        <Text fontSize="12.5px" fontWeight="600" lineHeight="1.3" noOfLines={2} color="gray.800">
                          {c.nome}
                        </Text>
                        {c.sectorName && c.sectorName !== c.nome && (
                          <Text fontSize="10px" color={colors.primaryColor} fontWeight="500" noOfLines={1}>
                            {c.sectorName}
                          </Text>
                        )}
                      </Box>
                      <Box
                        w="6px"
                        h="6px"
                        borderRadius="full"
                        bg={isSelected ? colors.primaryColor : 'transparent'}
                        flexShrink={0}
                        transition="background 0.15s"
                      />
                    </Flex>
                  );
                })}
              </React.Fragment>
            ))}
          </VStack>
        </Box>
      )}

      {/* ── Painel de planos ── */}
      {!loadingPlans && visiblePlans.length > 0 ? (
        <Card
          padding={{ base: '16px', md: '24px' }}
          flex={1}
          width={{ base: '100%', md: isInPlanosWhitelist ? '100%' : 'auto' }}
          overflow="hidden"
          borderRadius="16px"
          boxShadow="0 4px 20px rgba(0,0,0,0.08)"
        >
          <Tabs isFitted width="100%" variant="unstyled" index={selectedTabIndex} onChange={setSelectedTabIndex}>
            <TabList
              width="100%"
              overflowX="auto"
              bg="gray.50"
              borderRadius="14px"
              p="5px"
              gap={1}
              sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
            >
              {visiblePlans
                .filter((p: any) => {
                  if (!isInPlanosWhitelist) return true;
                  return p.name === 'Bronze' || disabledLevel === p.id;
                })
                .map((p: any) => {
                  const badge = badges.find((b) => b.name === p.name);
                  const isActive = disabledLevel === p.id;
                  return (
                    <Tab
                      key={p.id}
                      borderRadius="10px"
                      padding="8px 6px"
                      transition="all 0.2s cubic-bezier(0.4,0,0.2,1)"
                      border="2px solid"
                      borderColor={isActive ? 'green.300' : 'transparent'}
                      _selected={{
                        color: 'white',
                        backgroundColor: colors.primaryColor,
                        boxShadow: `0 4px 14px ${colors.primaryColor}45`,
                        transform: 'translateY(-1px)',
                        borderColor: 'transparent',
                      }}
                      sx={{ '&[aria-selected=true]': { borderColor: 'transparent' } }}
                    >
                      <VStack spacing={1} minH="66px" justify="center">
                        {badge && <Image src={`/${badge.image}`} alt={p.name} h="26px" objectFit="contain" />}
                        <Text
                          fontWeight="700"
                          fontSize="11px"
                          textAlign="center"
                          letterSpacing="0.2px"
                          lineHeight="1.2"
                        >
                          {p.name}
                        </Text>
                        {isActive && (
                          <Badge
                            bg="green.400"
                            color="white"
                            fontSize="8px"
                            borderRadius="full"
                            px={1.5}
                            py={0}
                            fontWeight="700"
                            textTransform="uppercase"
                            letterSpacing="0.3px"
                          >
                            Atual
                          </Badge>
                        )}
                      </VStack>
                    </Tab>
                  );
                })}
            </TabList>

            <TabPanels>
              {visiblePlans
                .filter((p: any) => {
                  if (!isInPlanosWhitelist) return true;
                  return p.name === 'Bronze' || disabledLevel === p.id;
                })
                .map((p: any) => {
                  const badge = badges.find((b) => b.name === p.name);
                  const conv = convenios?.find((con) => con.id?.toString() === selectedConvenio);
                  const planType = conv ? getPlanType(conv) : null;

                  const mainDescription = planType ? PLAN_TYPE_MESSAGES[planType][0](p) : null;
                  const isCurrentPlan = disabledLevel === p.id;
                  return (
                    <TabPanel key={p.id} px={0} pt={5} pb={0}>
                      <VStack spacing={5} w="100%" align="stretch">
                        {/* Banner categoria atual */}
                        {isCurrentPlan && (
                          <HStack
                            bg="green.50"
                            border="1px solid"
                            borderColor="green.200"
                            borderRadius="10px"
                            px={4}
                            py={2.5}
                            spacing={2.5}
                          >
                            <Box w="8px" h="8px" borderRadius="full" bg="green.400" flexShrink={0} />
                            <Text fontSize="13px" color="green.700" fontWeight="600">
                              Esta é sua categoria atual
                            </Text>
                          </HStack>
                        )}
                        {mainDescription && (
                          <Box
                            bg={`${colors.primaryColor}0D`}
                            borderRadius="10px"
                            p={3}
                            width="100%"
                            borderLeft="3px solid"
                            borderLeftColor={colors.primaryColor}
                          >
                            <Text fontSize="13.5px" fontWeight="500" color="gray.700" lineHeight="1.5">
                              {mainDescription}
                            </Text>
                          </Box>
                        )}
                        {/* Hero */}
                        <Box
                          bg={`linear-gradient(145deg, ${colors.primaryColor}10 0%, ${colors.primaryColor}03 100%)`}
                          borderRadius="16px"
                          p={{ base: '18px', md: '22px' }}
                          border="1px solid"
                          borderColor={`${colors.primaryColor}18`}
                          position="relative"
                          overflow="hidden"
                        >
                          {/* Decorative circle */}
                          <Box
                            position="absolute"
                            top="-20px"
                            right="-20px"
                            w="100px"
                            h="100px"
                            borderRadius="full"
                            bg={`${colors.primaryColor}08`}
                            pointerEvents="none"
                          />
                          <Flex align="flex-start" justify="space-between" gap={3} mb={4}>
                            <VStack spacing={1.5} align="flex-start">
                              <Heading
                                fontSize={{ base: '22px', md: '26px' }}
                                color="gray.900"
                                lineHeight={1}
                                letterSpacing="-0.5px"
                              >
                                {p.name}
                              </Heading>
                              {isCurrentPlan ? (
                                <Badge
                                  bg="green.50"
                                  color="green.600"
                                  borderRadius="full"
                                  px={2.5}
                                  py={0.5}
                                  fontSize="10px"
                                  fontWeight="600"
                                  border="1px solid"
                                  borderColor="green.200"
                                >
                                  ✓ Sua Categoria atual
                                </Badge>
                              ) : (
                                <Text fontSize="12px" color="gray.400" fontWeight="500">
                                  Categoria {p.name}
                                </Text>
                              )}
                            </VStack>
                            {badge && (
                              <Image
                                src={`/${badge.image}`}
                                alt={p.name}
                                h={{ base: '60px', md: '72px' }}
                                objectFit="contain"
                                flexShrink={0}
                                position="relative"
                              />
                            )}
                          </Flex>

                          <HStack spacing={0} align="flex-end" gap={0}>
                            <Text fontSize="16px" fontWeight="700" color="gray.500" lineHeight="1" mb="10px" mr="4px">
                              R$
                            </Text>
                            <Text
                              fontSize={{ base: '54px', md: '62px' }}
                              fontWeight="900"
                              color="gray.900"
                              lineHeight="1"
                              letterSpacing="-2px"
                            >
                              {p.value?.split('.')[0]}
                            </Text>
                            <VStack spacing={0} align="flex-start" mb="6px" ml="2px">
                              <Text fontSize="16px" fontWeight="700" color="gray.700" lineHeight="1">
                                ,{p.value?.split('.')[1]}
                              </Text>
                              <Text fontSize="11px" color="gray.400" fontWeight="500" lineHeight="1" mt="2px">
                                por mês
                              </Text>
                            </VStack>
                          </HStack>
                        </Box>

                        {/* Features */}
                        {conv && <PlanFeatures conv={conv} plan={p} />}

                        {/* CTA */}
                        {!isCurrentPlan && (
                          <VStack spacing={3} align="stretch" pt={1}>
                            <Divider />
                            <Checkbox isChecked={terms} onChange={(e) => setTerms(e.target.checked)} size="sm">
                              <Text fontSize="13px" color="gray.600">
                                Estou de acordo com os{' '}
                                <ExternalLink color={colors.primaryColor} to="/legal/termos-de-uso" target="blank">
                                  Termos de Uso
                                </ExternalLink>
                              </Text>
                            </Checkbox>
                            <Button isLoading={loading} onClick={() => openVindi(p)} isDisabled={!terms} width="100%">
                              Comprar
                            </Button>
                          </VStack>
                        )}
                      </VStack>
                    </TabPanel>
                  );
                })}
            </TabPanels>
          </Tabs>
        </Card>
      ) : (
        <Card
          padding={{ base: '20px', md: '30px' }}
          flex={1}
          width={{ base: '100%', md: isInPlanosWhitelist ? '100%' : 'auto' }}
          height={{ base: '30vh', md: '60vh' }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="16px"
        >
          {loadingPlans ? (
            <Loading />
          ) : (
            <VStack spacing={1.5}>
              <Text color="gray.400" fontSize="15px" fontWeight="500">
                {isServicesMode ? 'Nenhum serviço disponível' : 'Nenhum produto disponível'}
              </Text>
              <Text color="gray.300" fontSize="13px">
                Selecione outro item ao lado
              </Text>
            </VStack>
          )}
        </Card>
      )}
    </VStack>
  );
}
